import React from 'react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Settings } from 'lucide-react';
import { OPENAI_VOICES, RATE_OPTIONS } from './constants';

const NarrationSettings = ({
  narrationSource,
  onNarrationSourceChange,
  openAiVoice,
  onOpenAiVoiceChange,
  voices,
  voice,
  onBrowserVoiceChange,
  isSpanish,
  rate,
  onRateChange,
  humanAudioAvailable = false,
  isSignedIn = false,
  es = false,
}) => (
  <div className="border-t border-muted pt-4 space-y-3">
    <div className="flex items-center gap-2 text-sm font-medium">
      <Settings className="w-4 h-4" />
      {es ? 'Narración' : 'Narration'}
    </div>

    <div className="flex items-center justify-between">
      <span className="text-sm">{es ? 'Motor de voz' : 'Voice engine'}</span>
      <Select value={narrationSource} onValueChange={onNarrationSourceChange}>
        <SelectTrigger className="w-48" data-testid="narration-source-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="browser">{es ? 'Navegador (gratis)' : 'Browser (free)'}</SelectItem>
          {humanAudioAvailable && (
            <SelectItem value="human" data-testid="narration-human-option">
              {es ? 'Voz humana (KJV, gratis)' : 'Human voice (KJV, free)'}
            </SelectItem>
          )}
          <SelectItem value="openai">{es ? 'Lumera Premium (OpenAI)' : 'Lumera Premium (OpenAI)'}</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {narrationSource === 'human' ? (
      <p className="text-xs text-muted-foreground" data-testid="narration-human-note">
        {isSignedIn
          ? (es
            ? 'Narración real con voz humana de la KJV (dominio público). Reproduce el capítulo completo.'
            : 'Real human-voiced KJV narration (public domain). Plays the full chapter.')
          : (es
            ? 'Inicia sesión para escuchar la narración gratuita con voz humana.'
            : 'Sign in to listen to the free human-voiced narration.')}
      </p>
    ) : narrationSource === 'openai' ? (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm">{es ? 'Voz premium' : 'Premium voice'}</span>
          <Select value={openAiVoice} onValueChange={onOpenAiVoiceChange}>
            <SelectTrigger className="w-56" data-testid="openai-voice-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(isSpanish
                ? [...OPENAI_VOICES].sort((a, b) => (b.es === true) - (a.es === true))
                : OPENAI_VOICES
              ).map((v) => (
                <SelectItem key={v.id} value={v.id} data-testid={`openai-voice-${v.id}`}>
                  {v.label}{isSpanish && v.es ? ' · ✦' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isSpanish && (
          <p className="text-xs text-muted-foreground" data-testid="openai-spanish-hint">
            {es
              ? '✦ Recomendadas para una narración en español natural. Todas las voces leen en español.'
              : '✦ Recommended for natural Spanish narration. Every voice reads in Spanish.'}
          </p>
        )}
      </div>
    ) : (
      voices.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm">{es ? 'Voz del navegador' : 'Browser voice'}</span>
          <Select
            value={voice?.name || ''}
            onValueChange={(name) => {
              const v = voices.find((x) => x.name === name);
              if (v) onBrowserVoiceChange(v);
            }}
          >
            <SelectTrigger className="w-56" data-testid="browser-voice-select">
              <SelectValue placeholder={es ? 'Elige una voz' : 'Choose a voice'} />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {(() => {
                const langCode = isSpanish ? 'es' : 'en';
                const filtered = voices.filter((v) => v.lang.startsWith(langCode));
                const list = filtered.length > 0 ? filtered : voices;
                return list.map((v) => (
                  <SelectItem key={v.name} value={v.name}>
                    {v.name} ({v.lang})
                  </SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
        </div>
      )
    )}
    {isSpanish && narrationSource === 'browser' && voices.filter((v) => v.lang.startsWith('es')).length === 0 && (
      <p className="text-xs text-amber-600 dark:text-amber-400">
        {es
          ? 'Tu dispositivo no tiene una voz en español instalada; usa la voz Premium para una narración en español natural.'
          : 'Your device has no Spanish voice installed — use the Premium voice for natural Spanish narration.'}
      </p>
    )}

    <div className="flex items-center justify-between">
      <span className="text-sm">{es ? 'Velocidad' : 'Speed'}</span>
      <Select value={rate.toString()} onValueChange={(v) => onRateChange(parseFloat(v))}>
        <SelectTrigger className="w-32" data-testid="playback-rate-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RATE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

export default NarrationSettings;
